import axios, { AxiosInstance, AxiosError } from 'axios'
import { env } from '@/config/env'
import { useAuthStore } from '@/features/auth/stores/authStore'

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${env.apiUrl}/artwork-ms`,
  timeout: env.apiTimeout,
  withCredentials: true, // Send cookies for CORS requests
  headers: {
    'Content-Type': 'application/json',
  },
})

// Track when we last signed in to prevent immediate redirects after login
let lastSignInTime: number = 0
const GRACE_PERIOD_MS = 3000 // 3 seconds grace period for token propagation

export const setLastSignInTime = () => {
  lastSignInTime = Date.now()
}

// Get stored auth token
const getAuthToken = (): string | null => {
  return useAuthStore.getState().authToken
}

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Don't add Authorization header for signin requests
    // Stale/expired tokens would cause 401 before signin handler runs
    const isAuthRequest = config.url?.includes('/auth/signin')

    // An explicit Authorization from the caller wins.
    //
    // Masquerade and Supabase sign-in both ask "who is THIS token?" while the store still holds a
    // different one. Overwriting here answered with the wrong identity — beginning a masquerade
    // resolved the MANAGER rather than the artist, so the header showed the wrong person while
    // acting as them.
    const explicit = config.headers?.Authorization

    const token = getAuthToken()
    if (token && !isAuthRequest && !explicit) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Store auth token from signin response
    if (response.config?.url?.includes('/auth/signin') && response.status === 200) {
      const token = response.headers['x-access-token']
      if (token) {
        useAuthStore.getState().setToken(token)
      }
    }
    return response
  },
  (error: AxiosError) => {
    const now = Date.now()
    const timeSinceSignIn = now - lastSignInTime
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      const isSignInRequest = error.config?.url?.includes('/auth/signin')
      const isOnLoginPage = window.location.pathname === '/' || window.location.pathname === '/signin'
      const isRecentSignIn = timeSinceSignIn < GRACE_PERIOD_MS
      
      // Only clear auth and redirect if:
      // - We're NOT already on the login page
      // - This is NOT a sign-in request
      // - It's been more than grace period since last sign-in (prevents race conditions with token propagation)
      if (!isSignInRequest && !isOnLoginPage && !isRecentSignIn) {
        // Session expired or invalid - clear auth and redirect to login
        useAuthStore.getState().clearUser()
        if (window.location.pathname.startsWith('/app')) {
          window.location.href = '/'
        }
      }
      // Ignore 401s within grace period after sign-in (token propagation delay)
    }

    return Promise.reject(error)
  }
)

